import { useContext } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Typography } from "@mui/material";

import { theme } from "@bxtech/bx-ui-lib";

import { useEnterprises } from "../hooks/useEnterprises";
import { Enterprise } from "../types/dataTypes";
import { ProjectTypeType } from "../types/projects";
import {
  BXScoreReadmore,
  BXScoreReadmoreContainer,
} from "@/components/Farm/styles";
import { UserContext } from "@/context/UserContext";
import { extractRoleInfo } from "@/lib/api";
import i18n from "@/lib/i18n";
import { FarmType, RoleType, UserType } from "@/types/api";
import { ChartDatum } from "@/ui/Chart";

// check if id is valid
export const isValidId = (id: any) => {
  return !!id && id !== "undefined" && id.trim() !== "";
};

// convert SNAKE_CASE to Snake Case
export const convertSnakeCaseToText = (str?: string) => {
  if (!str) return "-";

  return str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const toSentenceCase = (str?: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getTerminologyPreferences = (user: UserType | null) => {
  const farmTerminologyRaw = convertSnakeCaseToText(
    user?.farmTerminologyPreference || "FARM"
  );
  const fieldTerminologyRaw = convertSnakeCaseToText(
    user?.fieldTerminologyPreference || "FIELD"
  );

  // Translate terminology based on user preference using i18n
  const farmTerminologyPreference = i18n.t(
    `terminology.farm.${farmTerminologyRaw.toLowerCase()}`
  );
  const fieldTerminologyPreference = i18n.t(
    `terminology.field.${fieldTerminologyRaw.toLowerCase()}`
  );

  // Translate plural forms
  const pluralFarmTerminologyPreference = i18n.t(
    `terminology.farm.${farmTerminologyRaw.toLowerCase()}_plural`
  );
  const pluralFieldTerminologyPreference = i18n.t(
    `terminology.field.${fieldTerminologyRaw.toLowerCase()}_plural`
  );

  return {
    farmTerminologyPreference,
    fieldTerminologyPreference,
    pluralFarmTerminologyPreference,
    pluralFieldTerminologyPreference,
  };
};

type BreadcrumbHistory = {
  label: string;
  link?: string;
  onClick?: () => void;
};

type UserHistory = BreadcrumbHistory & {
  permissions: RoleType[];
};

// get breadcrumb history depending on user Permissions
export const getBreadcrumbHistory = (
  history: UserHistory[],
  user: UserType | null
): BreadcrumbHistory[] => {
  const userRole = extractRoleInfo(user).roleType;

  if (!userRole) {
    return [];
  }

  return history.reduce((acc, curr) => {
    if (curr.permissions.includes(userRole)) {
      acc.push(curr);
    }

    return acc;
  }, [] as BreadcrumbHistory[]);
};

// Get the string of a month
export const threeLetterMonthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const reformatDatetime = (datetimeString: string): string => {
  const datetime = new Date(datetimeString);
  const day = datetime.getUTCDate();
  const month = threeLetterMonthNames[datetime.getUTCMonth()];
  const year = datetime.getUTCFullYear().toString().slice(-2);

  const newFormat = `${day} ${month} '${year}`;

  return newFormat;
};

export const reformatDatetimeUS = (datetimeString: string): string => {
  const datetime = new Date(datetimeString);
  const month = (datetime.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = datetime.getUTCDate().toString().padStart(2, "0");
  const year = datetime.getUTCFullYear().toString().slice(-2);
  return `${month}/${day}/${year}`;
};

export const reformatDatetimeLongUS = (datetimeString: string): string => {
  const datetime = new Date(datetimeString);
  const day = datetime.getUTCDate();
  const month = datetime.toLocaleString("en-US", { month: "long" });
  const year = datetime.getUTCFullYear();
  // Ordinal suffix logic
  const j = day % 10;
  const k = day % 100;
  let suffix = "th";
  if (j === 1 && k !== 11) suffix = "st";
  else if (j === 2 && k !== 12) suffix = "nd";
  else if (j === 3 && k !== 13) suffix = "rd";
  return `${day}${suffix} ${month} ${year}`;
};

export const renderBxScoreTooltipBody = (isAverageBxScore?: boolean) => {
  const { user } = useContext(UserContext);
  const { farmTerminologyPreference, pluralFarmTerminologyPreference } =
    getTerminologyPreferences(user);
  return isAverageBxScore ? (
    <Typography variant="body2">
      {i18n.t("dashboard.bxScoreTooltip.averageDescription", {
        farms: pluralFarmTerminologyPreference,
        farm: farmTerminologyPreference,
      })}
      <BXScoreReadmoreContainer
        onClick={() => {
          window.open(
            "https://knowledge.bx.tech/articles/what-is-the-bx-score/",
            "_blank"
          );
        }}
      >
        <BXScoreReadmore variant="subtitle2">
          {i18n.t("common.readMore")}
        </BXScoreReadmore>
        <ChevronRightIcon style={{ color: theme.palette.text.primary }} />
      </BXScoreReadmoreContainer>
    </Typography>
  ) : (
    <Typography variant="body2">
      {i18n.t("dashboard.bxScoreTooltip.singleDescription", {
        farm: farmTerminologyPreference,
      })}
      <BXScoreReadmoreContainer
        onClick={() => {
          window.open(
            "https://knowledge.bx.tech/articles/what-is-the-bx-score/",
            "_blank"
          );
        }}
      >
        <BXScoreReadmore variant="subtitle2">
          {i18n.t("common.readMore")}
        </BXScoreReadmore>
        <ChevronRightIcon style={{ color: theme.palette.text.primary }} />
      </BXScoreReadmoreContainer>
    </Typography>
  );
};

export const renderEnterpriseDashTooltipBody = (enterpriseName: string) => {
  const renderReadMoreLink = () => (
    <BXScoreReadmoreContainer
      onClick={() => {
        window.open(
          "https://knowledge.bx.tech/articles/what-is-the-bx-score/",
          "_blank"
        );
      }}
    >
      <BXScoreReadmore variant="subtitle2">
        {i18n.t("common.readMore")}
      </BXScoreReadmore>
      <ChevronRightIcon style={{ color: theme.palette.text.primary }} />
    </BXScoreReadmoreContainer>
  );

  return (
    <Typography variant="body2">
      {i18n.t("enterpriseDashboard.bxScore.tooltip", { enterpriseName })}
      {renderReadMoreLink()}
    </Typography>
  );
};

export const renderEnterpriseDashOrgTableTooltipBody = (farms: string) => {
  const renderReadMoreLink = () => (
    <BXScoreReadmoreContainer
      onClick={() => {
        window.open(
          "https://knowledge.bx.tech/articles/what-is-the-bx-score/",
          "_blank"
        );
      }}
    >
      <BXScoreReadmore variant="subtitle2">
        {i18n.t("common.readMore")}
      </BXScoreReadmore>
      <ChevronRightIcon style={{ color: theme.palette.text.primary }} />
    </BXScoreReadmoreContainer>
  );

  return (
    <Typography variant="body2">
      {i18n.t("enterpriseDashboard.orgTable.tooltip", { farms })}
      {renderReadMoreLink()}
    </Typography>
  );
};

export const calculateAverageBXScores = (farmData: FarmType[]) => {
  const scores = farmData.reduce(
    (acc, farm) => {
      if (farm.manualBXScore) {
        acc.bxScoreSum += farm.manualBXScore;
        acc.bxScoreCount += 1;
      }
      if (farm.manualBXPotentialScore) {
        acc.bxPotentialScoreSum += farm.manualBXPotentialScore;
        acc.bxPotentialScoreCount += 1;
      }
      return acc;
    },
    {
      bxScoreSum: 0,
      bxScoreCount: 0,
      bxPotentialScoreSum: 0,
      bxPotentialScoreCount: 0,
    }
  );

  const averageBXScore = scores.bxScoreCount
    ? Math.round(scores.bxScoreSum / scores.bxScoreCount)
    : undefined;
  const averagePotentialBXScore = scores.bxPotentialScoreCount
    ? Math.round(scores.bxPotentialScoreSum / scores.bxPotentialScoreCount)
    : undefined;
  return { averageBXScore, averagePotentialBXScore };
};

export const getTooltipWithReadmore = (
  tooltipText: string | React.ReactNode,
  readMoreLink: string,
  linkText?: string
): any => {
  return (
    <Typography variant="body2">
      {tooltipText}
      <BXScoreReadmoreContainer
        onClick={() => {
          window.open(readMoreLink, "_blank");
        }}
      >
        <BXScoreReadmore variant="subtitle2">
          {linkText || "Read more"}
        </BXScoreReadmore>
        <ChevronRightIcon style={{ color: theme.palette.text.primary }} />
      </BXScoreReadmoreContainer>
    </Typography>
  );
};

export const convertScoreToChartDatum = (
  scoreType: "potential" | "actual",
  score?: number
): ChartDatum[] => {
  if (score === undefined) {
    return [];
  }
  return [
    {
      value: score,
      label: scoreType === "actual" ? "Actual" : "Potential",
    },
  ];
};

export const convertToMillions = (value: number) => {
  return (value / 1000000).toFixed(2).toString();
};

export const isSuaveSuave = (userEmail?: string) => {
  const suaveEmails = ["erik@sauveranches.com"];
  return suaveEmails.includes(userEmail || "");
};

// TODO: remove this once the user state has been updated to rely on the database
export const isDemoEnterprise = (enterprise?: Enterprise): boolean => {
  return enterprise?.states?.isDemoEnterprise === true;
};

export const isMultiEnterpriseUser = (
  roleType: RoleType | null,
  userId?: number,
  enterprises?: Enterprise[]
): boolean => {
  let userEnterprises = enterprises;
  if (!enterprises) {
    userEnterprises = useEnterprises(userId).enterprises || [];
  }
  return (
    !!userId && roleType === "ENTERPRISE_ADMIN" && userEnterprises!.length > 1
  );
};
export const projectTypeDisplayName = (
  projectType?: ProjectTypeType
): string => {
  if (projectType === "Biochar" || projectType === "BiocharV2")
    return "Biochar";
  return projectType || "";
};

export const formatToTwoDecimalPlaces = (value?: number) => {
  if (!value) return 0;
  return parseFloat(value.toFixed(2));
};

export const ellipsis = (text: string, length: number) => {
  if (text.length <= length) {
    return text;
  }
  return `${text.slice(0, length)}...`;
};

// Helper to safely parse JSON strings
export const safeJSON = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    // Swallow JSON parse errors and return undefined
    return undefined;
  }
};
